import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ShiftMultiSelectHelperService } from '@wilson/shift-timeline/assignments-gantt-chart';
import { DateTimeFormat } from '@wilson/utils';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzListModule } from 'ng-zorro-antd/list';
import { AssignMultiSelectShiftsButtonComponent } from '../../timeline-multiselect/assign-multi-select-shifts-button/assign-multi-select-shifts-button.component';
import { DeleteMultiSelectShiftsButtonComponent } from '../../timeline-multiselect/delete-multi-select-shifts-button/delete-multi-select-shifts-button.component';
import { MoveMultiSelectShiftsButtonComponent } from '../../timeline-multiselect/move-multi-select-shifts-button/move-multi-select-shifts-button.component';
import { ReleaseMultiSelectShiftButtonComponent } from '../../timeline-multiselect/release-multi-select-shift-button/release-multi-select-shift-button.component';
import { UnassignMultiSelectShiftsButtonComponent } from '../../timeline-multiselect/unassign-multi-select-shifts-button/unassign-multi-select-shifts-button.component';
import { MultipleShiftsDrawerService } from './multiple-shifts-drawer.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { ManyEntity } from '@wilson/base';
import { Shift, Activity } from '@wilson/interfaces';
import { ActivitiesV2Gateway } from '@wilson/api/gateway';
import { RoleAction, RolePermissionSubject } from '@wilson/interfaces';
import { AbilityServiceSignal } from '@casl/angular';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { AddAttachmentsMultiSelectShiftsButtonComponent } from '../../timeline-multiselect/add-attachments-multi-select-shifts-button/add-attachments-multi-select-shifts-button.component';
import { AddShiftReportAttachmentsMultiSelectShiftsButtonComponent } from '../../timeline-multiselect/add-shift-report-attachments-multi-select-shifts-button/add-shift-report-attachments-multi-select-shifts-button.component';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'wilson-multiple-shifts-drawer',
  templateUrl: './multiple-shifts-drawer.component.html',
  styleUrls: ['./multiple-shifts-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NzSkeletonModule,
    CommonModule,
    TranslateModule,
    NzDrawerModule,
    NzButtonModule,
    NzListModule,
    RouterModule,
    FontAwesomeModule,
    FeatureFlagsModule,
    ReleaseMultiSelectShiftButtonComponent,
    AssignMultiSelectShiftsButtonComponent,
    MoveMultiSelectShiftsButtonComponent,
    UnassignMultiSelectShiftsButtonComponent,
    DeleteMultiSelectShiftsButtonComponent,
    AddAttachmentsMultiSelectShiftsButtonComponent,
    AddShiftReportAttachmentsMultiSelectShiftsButtonComponent,
  ],
})
export class MultipleShiftsDrawerComponent implements OnDestroy {
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  readonly store = inject(Store);
  readonly multipleShiftsDrawerService = inject(MultipleShiftsDrawerService);
  private readonly activitiesV2Gateway = inject(ActivitiesV2Gateway);

  shifts$ = this.multipleShiftsDrawerService.selectedShifts$;
  isDrawerVisible$ = this.shiftMultiSelectHelperService.selectedShifts$.pipe(
    map((shifts) => shifts.length > 1),
  );
  isLoading$ = this.multipleShiftsDrawerService.isLoading$;
  DateTimeFormat = DateTimeFormat;
  faTrashAlt = faTrashAlt;

  readonly isOperationOnGoingSignal = signal(false);

  @Output() deleteOperationTriggered = new EventEmitter();
  @Output() moveShiftOperationTriggered = new EventEmitter();
  @Output() assignShiftOperationTriggered = new EventEmitter();
  @Output() unassignShiftsOperationTriggered = new EventEmitter();

  protected readonly hasServiceActivitySignal = signal(false);
  protected readonly hasWilsonShareActivitySignal = signal(false);

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;
  private destroy$ = new Subject<void>();

  constructor() {
    this.initializeActivityStreams();
  }

  private initializeActivityStreams(): void {
    this.multipleShiftsDrawerService.selectedShifts$
      .pipe(
        filter((shifts) => shifts.length > 0),
        switchMap((shifts) => this.fetchActivities(shifts)),
        takeUntil(this.destroy$),
      )
      .subscribe((activities) => {
        this.hasServiceActivitySignal.set(
          activities.data.some((a) => a.serviceId),
        );
        this.hasWilsonShareActivitySignal.set(
          activities.data.some((a) => a.jobId),
        );
      });
  }

  private fetchActivities(
    shifts: ((Shift & { id: string }) | null)[],
  ): Observable<ManyEntity<Activity>> {
    const shiftIds = shifts.reduce((acc, shift) => {
      if (shift && !!shift.id) {
        acc.push(shift.id);
      }
      return acc;
    }, [] as string[]);

    if (shiftIds.length === 0) {
      return new BehaviorSubject<ManyEntity<Activity>>({ data: [], count: 0 });
    } else {
      return this.activitiesV2Gateway
        .getActivities({
          options: {
            where: { shiftId: { in: shiftIds } },
            pageSize: 9999,
          },
        })
        .pipe(
          catchError(() => {
            this.nzMessageService.error(
              this.translate.instant('general.something_went_wrong'),
            );
            return of({ data: [], count: 0 });
          }),
        );
    }
  }

  async unselect(shiftId: string): Promise<void> {
    await this.shiftMultiSelectHelperService.unselect(shiftId);
  }

  closeDrawer(): void {
    this.shiftMultiSelectHelperService.clearSelection();
  }

  refreshDrawer(): void {
    this.multipleShiftsDrawerService.refreshDrawerData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
